import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  IconButton,
  Avatar,
  keyframes,
} from '@mui/material';
import { Close, PersonAdd, Done, DoneAll } from '@mui/icons-material';
import { Logo } from '../widgets/Logo';
import CustomButton from '../widgets/CustomButton';
import BottomSheet from './BottomSheet';
import { glassStyles } from '../../tokens/designTokens';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useAuth } from '../../hooks/useAuth';
import InitialsAvatar from '../widgets/InitialsAvatar';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';

interface SharedUserInfo {
  name: string;
  username?: string;
  avatar?: string;
  uid: string;
}

interface SharedWatchlistInfo {
  name: string;
  description?: string;
  movieCount?: number;
}

interface SharedListIntroBottomSheetProps {
  open: boolean;
  onClose: () => void;
  onShowWatchlist: () => void;
  sharedBy?: SharedUserInfo;
  watchlistInfo?: SharedWatchlistInfo;
}

// Animation keyframes
const highlightPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(125, 122, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 8px rgba(125, 122, 255, 0.2);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(125, 122, 255, 0);
  }
`;

const followingSuccess = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.2) rotate(-5deg);
  }
  50% {
    transform: scale(1.3) rotate(5deg);
  }
  75% {
    transform: scale(1.1) rotate(-2deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
`;

const SharedListIntroBottomSheet: React.FC<SharedListIntroBottomSheetProps> = ({
  open,
  onClose,
  onShowWatchlist,
  sharedBy,
  watchlistInfo,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { followUser, unfollowUser, isUserFollowed } = useUserFollow();
  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();

  // Animation states
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [localFollowingState, setLocalFollowingState] = useState<boolean | null>(null);

  // Debug logging to help troubleshoot issues
  useEffect(() => {
    if (open) {
      console.log('SharedListIntroBottomSheet: Props received:', {
        sharedBy: sharedBy ? {
          name: getUserDisplayName(sharedBy.uid, sharedBy.name),
          username: sharedBy.username,
          uid: sharedBy.uid,
          hasAvatar: !!(getUserAvatar(sharedBy.uid) || sharedBy.avatar)
        } : null,
        watchlistInfo: watchlistInfo ? {
          name: watchlistInfo.name,
          description: watchlistInfo.description,
          movieCount: watchlistInfo.movieCount
        } : null
      });
    }
  }, [open, sharedBy, watchlistInfo]);

  // Calculate derived values - use local state if available, otherwise context state
  const contextIsFollowing = sharedBy ? isUserFollowed(sharedBy.uid) : false;
  const isFollowing = localFollowingState !== null ? localFollowingState : contextIsFollowing;
  const showFollowButton = sharedBy && user && sharedBy.uid !== user.uid;

  const handleShowWatchlist = () => {
    onShowWatchlist();
    onClose();
  };

  const handleFollowToggle = async () => {
    if (!sharedBy || !user || isAnimating) return;
    
    setIsAnimating(true);
    const wasFollowing = isFollowing;
    
    // Immediately update local state for instant UI feedback
    setLocalFollowingState(!wasFollowing);
    
    try {
      if (wasFollowing) {
        await unfollowUser(sharedBy.uid);
      } else {
        await followUser(sharedBy.uid);
        // Show success animation when following
        setShowSuccessAnimation(true);
        setTimeout(() => setShowSuccessAnimation(false), 600);
      }
    } catch (error) {
      console.error('Error toggling follow state:', error);
      // Revert local state on error
      setLocalFollowingState(wasFollowing);
    } finally {
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  // Sync local state with context state when context updates
  useEffect(() => {
    if (localFollowingState !== null && localFollowingState === contextIsFollowing) {
      // Context has caught up with local state, reset local state
      setLocalFollowingState(null);
    }
  }, [contextIsFollowing, localFollowingState]);

  // Effect to start repeating highlight pulse animation
  useEffect(() => {
    if (open && showFollowButton && !isFollowing) {
      // Reset animation state after a short delay to ensure clean start
      const timer = setTimeout(() => {
        setIsAnimating(false); // Reset any previous animation state
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [open, showFollowButton, isFollowing]);

  // Shared content component
  const content = (
    <Box sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        position: 'relative',
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Title with logo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
            py: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '28px',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              Welcome to
            </Typography>
            <Logo variant="default" size="medium" state="animated" />
          </Box>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: 'rgba(255, 255, 255, 0.8)',
              py: 1,
            }}
          >
            {sharedBy?.name 
              ? `${getUserDisplayName(sharedBy.uid, sharedBy.name)} has shared ${watchlistInfo?.name ? `"${watchlistInfo.name}"` : 'a movie watchlist'} with you!`
              : 'Someone has shared a curated movie watchlist with you :)'
            }
          </Typography>

          {/* User Card */}
          {sharedBy && (
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                padding: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                height: 72,
                p: 2.5,
              }}
            >
              {/* User Avatar */}
              <InitialsAvatar
                src={getUserAvatar(sharedBy.uid) || sharedBy.avatar}
                name={getUserDisplayName(sharedBy.uid, sharedBy.name)}
                size={48}
                sx={{ opacity: 0.8 }}
              />

              {/* User Info */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 0.5, 
                }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '16px',
                    lineHeight: '24px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                  }}
                >
                  {getUserDisplayName(sharedBy.uid, sharedBy.name)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  @{sharedBy.username || getUserDisplayName(sharedBy.uid, sharedBy.name)?.toLowerCase().replace(/\s+/g, '') || 'user'}
                </Typography>
              </Box>

              {/* Follow Button */}
              {showFollowButton && (
                <IconButton
                  onClick={handleFollowToggle}
                  disabled={isAnimating}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: isFollowing ? '#30D158' : '#7D7AFF',
                    borderRadius: 1.5,
                    padding: 1,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
                    animation: !isFollowing && open ? `${highlightPulse} 2s ease-in-out infinite` : 'none',
                    '&:hover': {
                      backgroundColor: isFollowing ? '#28B946' : '#6B68E6',
                      transform: !isAnimating ? 'scale(1.05)' : 'scale(0.95)',
                    },
                    '&:disabled': {
                      backgroundColor: isFollowing ? '#30D158' : '#7D7AFF',
                      opacity: 0.8,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      animation: showSuccessAnimation ? `${followingSuccess} 0.6s ease-out` : 'none',
                    }}
                  >
                    {isFollowing ? (
                      <DoneAll 
                        sx={{ 
                          color: 'rgba(0, 0, 0, 0.9)', 
                          fontSize: 20,
                          transition: 'all 0.3s ease',
                        }} 
                      />
                    ) : (
                      <PersonAdd 
                        sx={{ 
                          color: '#323232', 
                          fontSize: 20,
                          transition: 'all 0.3s ease',
                        }} 
                      />
                    )}
                  </Box>
                </IconButton>
              )}
            </Box>
          )}
        </Box>

      {/* View Watchlist Button */}
      <CustomButton
        semantic="brand"
        onClick={handleShowWatchlist}
        sx={{
          width: '100%',
          height: 56,
          backgroundColor: '#ff6482',
          color: 'rgba(0, 0, 0, 0.9)',
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          borderRadius: 2.5,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: '#e55a75',
          },
        }}
      >
        View watchlist
      </CustomButton>
    </Box>
  );

  // Mobile Bottom Sheet
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        maxHeight="80vh"
        zIndex={1500}
      >
        {/* Drag handle */}
        <Box sx={{
          width: 40,
          height: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
          borderRadius: 2,
          alignSelf: 'center',
          marginTop: 1.5,
          marginBottom: 0.5,
        }} />

        {content}
      </BottomSheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...glassStyles.popup(isDark),
          maxHeight: '85vh',
        }
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <Close />
      </IconButton>

      {content}
    </Dialog>
  );
};

export default SharedListIntroBottomSheet; 